import type { ErrorEvent } from "@sentry/browser";
import { isTRPCClientError } from "@trpc/client";

const TRPC_FINGERPRINT_PREFIX = "trpc-client-error";
const UNKNOWN_CODE = "UNKNOWN";

/**
 * Groups tRPC client errors by the procedure that failed and its error code.
 *
 * Sentry groups by stack trace, and every `TRPCClientError` is created in the
 * same tRPC link frames no matter which procedure failed. Without this, a
 * job-post validation failure, an authorization rejection and a chat error all
 * land in one issue (SAILE-WEB-ER). The message is left out of the fingerprint
 * because it often embeds IDs or serialized validation issues, which would
 * split one problem into many issues.
 *
 * Errors without a procedure path, such as a failed request with no tRPC
 * response, keep Sentry's default grouping.
 */
export function applyTrpcErrorGrouping(
  event: ErrorEvent,
  originalException: unknown,
): ErrorEvent {
  if (!isTRPCClientError(originalException)) {
    return event;
  }

  const path = originalException.data?.path;
  if (typeof path !== "string" || path.length === 0) {
    return event;
  }

  const code = originalException.data?.code ?? UNKNOWN_CODE;
  return {
    ...event,
    fingerprint: [TRPC_FINGERPRINT_PREFIX, path, code],
    tags: { ...event.tags, "trpc.path": path, "trpc.code": code },
  };
}
